import { apiRequest } from "./queryClient";

// Helper function to get auth headers with tenant ID
export function getAuthHeaders(): Record<string, string> {
  return {
    'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`,
    'X-Tenant-Id': localStorage.getItem('tenant_id') || '',
    'Content-Type': 'application/json',
  };
}

export interface DashboardMetrics {
  dailyVisits: number;
  activeSessions: number;
  totalWeight: number;
  averageTime: number;
}

export interface Visit {
  id: number;
  tenantId: string;
  siteId: number;
  subjectType: string;
  subjectExternalId: string;
  plateNumber?: string;
  driverName?: string;
  status: string;
  accessDecision?: string;
  reasons?: string;
  startedAt: string;
  endedAt?: string;
  ocrRequested?: boolean;
  ocrRequestedAt?: string | Date | null;
  ocrProcessed?: boolean;
  ocrProcessedAt?: string | Date | null;
  ocrConfidence?: number | null;
}

export interface WeighSession {
  id: number;
  tenantId: string;
  visitId: number;
  laneId: number;
  orderExternalId?: string;
  status: string;
  tareWeight?: string;
  grossWeight?: string;
  netWeight?: string;
  unit: string;
  scaleDeviceId?: string;
  createdAt: string;
  updatedAt: string;
}

export const api = {
  // Orders
  getOrders: (): Promise<any[]> =>
    fetch("/api/orders", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("auth_token") || ""}`,
        "X-Tenant-Id": localStorage.getItem("tenant_id") || ""
      }
    }).then(r => {
      if (!r.ok) throw new Error(`${r.status}: ${r.statusText}`);
      return r.json();
    }),
  // Dashboard
  getDashboardMetrics: (): Promise<DashboardMetrics> =>
    fetch("/api/dashboard/metrics", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("auth_token") || ""}`,
        "X-Tenant-Id": localStorage.getItem("tenant_id") || ""
      }
    }).then(r => {
      if (!r.ok) throw new Error(`${r.status}: ${r.statusText}`);
      return r.json();
    }),

  getActiveVisits: (limit?: number, offset?: number): Promise<any[]> => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    
    return fetch(`/api/dashboard/active-visits?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("auth_token") || ""}`,
        "X-Tenant-Id": localStorage.getItem("tenant_id") || ""
      }
    }).then(r => {
      if (!r.ok) throw new Error(`${r.status}: ${r.statusText}`);
      return r.json();
    });
  },

  // Visits
  getVisits: (): Promise<Visit[]> =>
    fetch("/api/visits", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("auth_token") || ""}`,
        "X-Tenant-Id": localStorage.getItem("tenant_id") || ""
      }
    }).then(r => {
      if (!r.ok) throw new Error(`${r.status}: ${r.statusText}`);
      return r.json();
    }),

  createVisit: (data: {
    siteId: number;
    subjectType: string;
    subjectExternalId: string;
    plateNumber?: string;
    driverName?: string;
  }): Promise<{ status: string; visit: Visit; next: string }> =>
    apiRequest("POST", "/api/visits/entry", data).then(r => r.json()),

  // Sessions
  getSessions: (): Promise<WeighSession[]> =>
    fetch("/api/sessions", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("auth_token") || ""}`,
        "X-Tenant-Id": localStorage.getItem("tenant_id") || ""
      }
    }).then(r => {
      if (!r.ok) throw new Error(`${r.status}: ${r.statusText}`);
      return r.json();
    }),

  createSession: (data: {
    visitId: number;
    laneId: number;
    orderExternalId?: string;
  }): Promise<WeighSession> =>
    apiRequest("POST", "/api/sessions", data).then(r => r.json()),

  addReading: (sessionId: number, data: {
    type: string;
    value: number;
    stabilityMs?: number;
    raw?: string;
  }) =>
    apiRequest("POST", `/api/sessions/${sessionId}/readings`, data).then(r => r.json()),

  closeSession: (sessionId: number) =>
    apiRequest("POST", `/api/sessions/${sessionId}/close`, {}).then(r => r.json()),
};
